import Button from "@components/Button";
import Icon from "@components/Icon";
import { DASHBOARD, PRICING, SIGNUP } from "@constants/internal-route.constant";
import { useAuth } from "@context/Auth";
import ConsolePage from "@layout/console/ConsolePage";
import { IPublicModule } from "@interface/billing.interface";
import { ModuleService } from "@services/api/Module.service";
import { ENV } from "config/ENV.config";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { appIcon } from "utility/appIcons";

export default function Home() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // ErpBoi consoles — the "books" this account opens (from public.modules).
  const [modules, setModules] = useState<IPublicModule[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Codes of the modules this company actually holds. The catalog above is public and
  // company-agnostic, so without this the hub offers "Open console" for apps that were
  // never subscribed to — portal now refuses those a token, so the button would only bounce.
  const [ownedCodes, setOwnedCodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    ModuleService.getPublicList()
      .then((res) => setModules(res?.body || []))
      .catch(() => setModules([]));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setOwnedCodes(new Set());
      return;
    }
    ModuleService.getPurchasedModules()
      .then((res) =>
        setOwnedCodes(
          new Set(
            (res?.body || [])
              .map((m: any) => m?.code?.toUpperCase())
              .filter(Boolean)
          )
        )
      )
      .catch(() => setOwnedCodes(new Set()));
  }, [isAuthenticated]);

  const isOwned = (m: IPublicModule) => ownedCodes.has(m.code?.toUpperCase());

  const toggle = (code: string) =>
    setExpanded((prev) => ({ ...prev, [code]: !prev[code] }));

  const gotoConsole = (m: IPublicModule) => {
    if (isAuthenticated) {
      const key = m.code?.toLowerCase();
      // env-driven per-product console URL (localhost in dev); fall back to host_origin
      const url = ENV.consoles?.[key] || `https://${m.hostOrigin}`;
      window.open(url, "_self");
    } else {
      navigate(SIGNUP);
    }
  };

  // Signed in, "/" is where login lands: the console's own directory of apps. Signed out, it is
  // the public product page (PublicLayout).
  if (isAuthenticated)
    return (
      <ConsolePage
        title="Your consoles"
        subtitle="You’re signed in across the whole suite — open any console below."
        crumbs={[{ label: "Consoles" }]}
      >
        <div className="row g-3">
          {modules.map((m) => (
            <div className="col-12 col-md-6 col-xl-4" key={m.code}>
              <div className={`card h-100 mb-0${isOwned(m) ? " card-outline card-primary" : ""}`}>
                <div className="card-body d-flex flex-column">
                  <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                    <h5 className="mb-0">{m.title}</h5>
                    {isOwned(m) ? (
                      <span className="badge text-bg-success">Active</span>
                    ) : (
                      <span className="badge text-bg-light border">Not added</span>
                    )}
                  </div>
                  <div className="small text-body-secondary mb-2">{m.hostOrigin || m.code}</div>
                  {m.description ? <p className="small mb-3">{m.description}</p> : null}
                  <div className="mt-auto">
                    {isOwned(m) ? (
                      <Button color="primary" className="w-100" onClick={() => gotoConsole(m)}>
                        Open console <Icon icon="box-arrow-up-right" />
                      </Button>
                    ) : (
                      <Button color="primary" variant="outline" className="w-100" onClick={() => navigate(DASHBOARD)}>
                        <Icon icon="plus-lg" /> Activate
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ConsolePage>
    );

  return (
    <>
      <section className="text-center py-4 py-md-5">
        <div className="text-uppercase small fw-semibold text-body-secondary mb-2">The {ENV.brandName} suite</div>
        <h1 className="display-6 fw-semibold mb-3">Open your business’s books.</h1>
        <p className="lead text-body-secondary mx-auto mb-4 erp-public-lead">
          Sales, people, accounts and files. One account for everything your business runs on.
        </p>
        <div className="d-flex flex-wrap justify-content-center gap-2">
          <Button color="primary" size="lg" onClick={() => navigate(SIGNUP)}>
            <Icon icon="rocket-takeoff" /> Start a 30-day trial
          </Button>
          <Button variant="outline" color="secondary" size="lg" onClick={() => navigate(PRICING)}>
            See pricing
          </Button>
        </div>
      </section>

      <div className="d-flex align-items-baseline justify-content-between mb-3">
        <h2 className="h5 mb-0">The apps</h2>
        <span className="small text-body-secondary">{modules.length} apps, one sign-in</span>
      </div>
      <div className="row g-3">
        {modules.map((m) => (
          <div className="col-12 col-md-6 col-xl-4" key={m.code}>
            <div className="card h-100 mb-0">
              <div className="card-body d-flex flex-column">
                <div className="d-flex align-items-center gap-3 mb-2">
                  <span className="erp-app-icon">
                    <Icon icon={appIcon(m.code)} size={20} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="h6 mb-0">{m.title}</h3>
                    <div className="small text-body-secondary text-truncate">{m.hostOrigin || m.code}</div>
                  </div>
                </div>
                {m.description ? <p className="small text-body-secondary mb-2">{m.description}</p> : null}
                {expanded[m.code] && m.features?.length ? (
                  <ul className="small ps-3 mb-2">
                    {m.features.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                ) : null}
                {m.features?.length ? (
                  <div className="mt-auto pt-1">
                    <button type="button" className="btn btn-link btn-sm px-0" onClick={() => toggle(m.code)}>
                      {expanded[m.code] ? "Show less" : "Show more"}
                      <Icon icon={expanded[m.code] ? "chevron-up" : "chevron-down"} size={12} className="ms-1" />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
